import React, { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  createEmptyTerms,
  fetchTerms,
  fetchWorks,
  taxonomies,
  TaxonomyKey,
  Terms,
  Work,
  WorksSearchParameters,
} from './api';
import { createContext, toQueryString, useInitialSearchParameters } from './utils';

const [termsContext, useTerms] = createContext<Terms>();

export const WorksTermsProvider: FC<{ children: ReactNode }> = (props) => {
  const { children } = props;
  const [terms, setTerms] = useState<Terms>(createEmptyTerms());

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      const keys = Object.keys(taxonomies) as TaxonomyKey[];
      const newValue = createEmptyTerms();
      await Promise.all(
        keys.map((key) =>
          fetchTerms(taxonomies[key], controller.signal).then((res) => {
            newValue[key] = res;
          }),
        ),
      );
      setTerms((state) => ({ ...state, ...newValue }));
    })();
    return () => {
      controller.abort();
    };
  }, []);

  return <termsContext.Provider value={terms}>{children}</termsContext.Provider>;
};

const [worksContext, useWorks] = createContext<Work[]>();

const [totalWorksContext, useTotalWorks] = createContext<number>();

const [loadingContext, useLoading] = createContext<boolean>();

const [searchParametersContext, useSearchParameters] = createContext<WorksSearchParameters>();

const [setSearchParametersContext, useSetSearchParameters] =
  createContext<(search: WorksSearchParameters) => void | Promise<void>>();

export const WorksProvider: FC<{ children: ReactNode }> = (props) => {
  const { children } = props;
  const location = useLocation();
  const initParameters = useInitialSearchParameters();

  const { pathname } = location;

  const [search, setSearch] = useState<WorksSearchParameters>(initParameters);
  const [works, setWorks] = useState<Work[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const history = useHistory<WorksSearchParameters>();

  const firstRef = useRef(true);

  const setSearchParameters = useCallback(
    (value: WorksSearchParameters) => {
      setLoading(true);
      const params = toQueryString(value);
      if (firstRef.current) {
        history.replace(`${pathname}?${params.toString()}`, value);
      } else {
        history.push(`${pathname}?${params.toString()}`, value);
      }
      firstRef.current = false;
    },
    [pathname, history],
  );

  useEffect(() => {
    const controller = new AbortController();
    const cleanup = history.listen(async (loc) => {
      if (!loc.state) {
        return;
      }
      setSearch(loc.state);
      const res = await fetchWorks(loc.state, controller.signal);
      setTotal(res.count);
      setWorks(res.body);
      setLoading(false);
    });
    setSearchParameters(search);
    return () => {
      cleanup();
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <setSearchParametersContext.Provider value={setSearchParameters}>
      <searchParametersContext.Provider value={search}>
        <worksContext.Provider value={works}>
          <totalWorksContext.Provider value={total}>
            <loadingContext.Provider value={loading}>{children}</loadingContext.Provider>
          </totalWorksContext.Provider>
        </worksContext.Provider>
      </searchParametersContext.Provider>
    </setSearchParametersContext.Provider>
  );
};

const useSearchCondition = (): string => {
  const terms = useTerms();
  const search = useSearchParameters();

  const names: string[] = [];

  search.industry.reduce<string[]>((prev, id) => {
    const term = terms.industry.find((e) => e.id === id);
    if (term) {
      prev.push(term.name);
    }
    return prev;
  }, names);

  search.option.reduce<string[]>((prev, id) => {
    const term = terms.option.find((e) => e.id === id);
    if (term) {
      prev.push(term.name);
    }
    return prev;
  }, names);

  search.taste.reduce<string[]>((prev, id) => {
    const term = terms.taste.find((e) => e.id === id);
    if (term) {
      prev.push(term.name);
    }
    return prev;
  }, names);

  return names.join(', ');
};

export {
  useTerms,
  useSearchParameters,
  useSetSearchParameters,
  useWorks,
  useTotalWorks,
  useLoading,
  useSearchCondition,
};
